<template>
  <div class="payment-container">
    <!-- Show loading state when payment info is not yet loaded -->
    <div v-if="isLoading" class="loading-state">
      <p>Đang tải thông tin thanh toán...</p>
    </div>

    <!-- Show error state -->
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadBookingData">Thử lại</button>
      <button class="cancel-btn" @click="goBack">Quay lại</button>
    </div>

    <!-- Show warning state when data is incomplete -->
    <div v-else-if="isDataIncomplete" class="data-warning">
      <p>
        <i class="fas fa-exclamation-triangle"></i>
        Dữ liệu thanh toán không đầy đủ. Vui lòng liên hệ nhà cung cấp dịch vụ
        hoặc thử lại sau.
      </p>
    </div>

    <!-- Only render when payment info is available -->
    <div v-else class="payment-summary travel-ticket">
      <div class="ticket-hero">
        <div>
          <div class="ticket-eyebrow">DiChungXe · giữ chỗ</div>
          <h2>Vé đang chờ thanh toán</h2>
          <p>Kiểm tra thông tin, áp dụng ưu đãi và chuyển khoản đúng nội dung để hệ thống xác nhận tự động.</p>
        </div>
        <div class="ticket-countdown">
          <span>Phiên còn lại</span>
          <strong>{{ paymentCountdown || '--:--' }}</strong>
        </div>
      </div>

      <div class="booking-details">
        <div class="trip-info">
          <h3>Chi tiết chuyến đi</h3>
          <p>
            <strong>Điểm đến:</strong>
            {{ destinationLocation || 'Chưa cung cấp' }}
          </p>
          <p>
            <strong>Thời gian:</strong>
            {{ formattedDepartureTime || 'Chưa cung cấp' }}
          </p>
          <p><strong>Loại vé:</strong> {{ ticketType || 'Vé thường' }}</p>
          <p v-if="!isSpecialTicketType">
            <strong>Số ghế:</strong>
            {{ seatNumbers.length ? seatNumbers.join(', ') : 'Chưa cung cấp' }}
          </p>
          <p v-else>
            <strong>Số lượng vé:</strong>
            {{ specialTicketQuantity }}
          </p>
        </div>

        <div class="customer-info">
          <h3>Thông tin khách hàng</h3>

          <!-- Primary customer contact info -->
          <div class="primary-contact">
            <p>
              <strong>Liên hệ:</strong> {{ customerName || 'Chưa cung cấp' }}
            </p>
            <p>
              <strong>Số điện thoại:</strong>
              {{ customerPhone || 'Chưa cung cấp' }}
            </p>
            <p v-if="customerEmail">
              <strong>Email:</strong> {{ customerEmail }}
            </p>
          </div>

          <!-- All passengers info -->
          <div class="all-passengers" v-if="!isSpecialTicketType">
            <h4>Danh sách hành khách ({{ passengers.length }})</h4>

            <div class="passenger-tabs">
              <div
                v-for="(passenger, index) in passengers"
                :key="index"
                :class="[
                  'passenger-tab',
                  { active: activePassengerIndex === index }
                ]"
                @click="activePassengerIndex = index"
              >
                <span>{{ index + 1 }}. {{ passenger.name }}</span>
              </div>
            </div>

            <div class="passenger-details" v-if="currentPassenger">
              <p><strong>Họ tên:</strong> {{ currentPassenger.name }}</p>
              <p v-if="currentPassenger.birthYear">
                <strong>Năm sinh:</strong> {{ currentPassenger.birthYear }}
              </p>
              <p>
                <strong>Ghế ngồi:</strong> {{ currentPassenger.seatNumber }}
              </p>
              <p v-if="currentPassenger.pickupLocationName">
                <strong>Điểm đón:</strong>
                {{ currentPassenger.pickupLocationName }}
              </p>
            </div>
          </div>
          <div class="all-passengers" v-else>
            <div class="special-ticket-info">
              <h3>Thông tin vé {{ ticketType }}</h3>
              <p><strong>Số lượng vé:</strong> {{ specialTicketQuantity }}</p>
            </div>
          </div>
        </div>

        <div class="addon-info" v-if="addons && addons.length > 0">
          <h3>Dịch vụ bổ sung</h3>
          <div class="addon-items">
            <div
              v-for="(addon, index) in addons"
              :key="index"
              class="addon-item"
            >
              <div class="addon-name">{{ addon.name }}</div>
              <div class="addon-quantity">x{{ addon.quantity }}</div>
              <div class="addon-price">
                {{ formatCurrency(addon.total_price) }}
              </div>
            </div>
          </div>
          <div class="addon-total">
            <span>Tổng phụ thu:</span>
            <span class="addon-total-amount">{{
              formatCurrency(addonsTotal)
            }}</span>
          </div>
        </div>

        <div class="price-summary">
          <h3>Tổng thanh toán</h3>
          <div class="price-breakdown">
            <div class="price-item">
              <span>Tiền vé:</span>
              <span>{{ formatCurrency(ticketTotal) }}</span>
            </div>
            <div class="price-item" v-if="addons && addons.length > 0">
              <span>Phụ thu dịch vụ:</span>
              <span>{{ formatCurrency(addonsTotal) }}</span>
            </div>
            <div class="price-item discount" v-if="hasAppliedDiscount">
              <span>Giảm giá:</span>
              <span>-{{ formatCurrency(discountAmount) }}</span>
            </div>
            <div class="price-item total">
              <span>Tổng cộng:</span>
              <span class="total-amount">
                {{ formatCurrency(displayAmount) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="ticket-divider" aria-hidden="true"></div>

      <div class="payment-methods">
        <div class="discount-section" :class="{ applied: hasAppliedDiscount }">
          <div class="discount-heading">
            <div>
              <span class="discount-kicker">Mã giảm giá</span>
              <h3>Ưu đãi chuyến đi</h3>
            </div>
            <div v-if="hasAppliedDiscount" class="discount-pill">
              <i class="fas fa-check"></i>
              {{ discountCode.toUpperCase() }} · tiết kiệm {{ formatCurrency(discountAmount) }}
            </div>
          </div>

          <div class="discount-form">
            <input
              type="text"
              v-model="discountCode"
              placeholder="Nhập mã ưu đãi"
              class="discount-input"
              :disabled="isApplyingDiscount || hasAppliedDiscount"
            />
            <button
              @click="applyDiscountCode"
              class="apply-discount-btn"
              :disabled="!discountCode || isApplyingDiscount || hasAppliedDiscount"
              :class="{ applying: isApplyingDiscount }"
            >
              <span v-if="!isApplyingDiscount">Áp dụng</span>
              <span v-else><i class="fas fa-spinner fa-spin"></i> Đang xử lý</span>
            </button>
          </div>

          <div
            v-if="discountMessage"
            :class="['discount-message', { error: !hasAppliedDiscount, success: hasAppliedDiscount }]"
          >
            {{ discountMessage }}
          </div>

          <div v-if="hasAppliedDiscount" class="discount-summary">
            <div class="discount-info">
              <div class="discount-row">
                <span>Tổng tiền gốc</span>
                <span>{{ formatCurrency(originalAmount) }}</span>
              </div>
              <div class="discount-row highlight">
                <span>Ưu đãi đã áp dụng</span>
                <span>-{{ formatCurrency(discountAmount) }}</span>
              </div>
              <div class="discount-row total">
                <span>Cần thanh toán</span>
                <span>{{ formatCurrency(finalAmount) }}</span>
              </div>
            </div>
            <button @click="removeDiscountCode" class="remove-discount-btn">
              <i class="fas fa-times"></i> Hủy mã
            </button>
          </div>
        </div>

        <div class="bank-heading">
          <span>Thanh toán ngân hàng</span>
          <h3>Thông tin chuyển khoản</h3>
        </div>

        <!-- Phần hiển thị thanh toán chuyển khoản ngân hàng -->
        <div class="bank-transfer-details">
          <div class="qr-section">
            <h4>Quét mã QR để thanh toán</h4>
            <div class="qr-code">
              <img :src="qrCodeUrl" alt="QR Code thanh toán" class="qr-image" />
              <div class="qr-note">
                Sử dụng App Banking quét mã để thanh toán nhanh
              </div>
            </div>
          </div>

          <div class="transfer-info">
            <h4>Thông tin chuyển khoản</h4>
            <div class="info-item">
              <span class="label">Ngân hàng:</span>
              <span class="value">{{ bankName }}</span>
            </div>
            <div class="info-item">
              <span class="label">Số tài khoản:</span>
              <span class="value">{{ accountNumber }}</span>
            </div>
            <div class="info-item">
              <span class="label">Chủ tài khoản:</span>
              <span class="value">{{ accountName }}</span>
            </div>
            <div class="info-item amount-item">
              <span class="label">Số tiền:</span>
              <span class="value highlight-amount">{{
                formatCurrency(displayAmount)
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">Nội dung CK:</span>
              <span class="value">{{ transferContent }}</span>
            </div>

            <div class="transfer-note">
              <i class="fas fa-info-circle"></i>
              <span
                >Vui lòng chuyển khoản đúng số tiền và nội dung để đơn hàng được
                xử lý tự động.</span
              >
            </div>

            <div class="confirmation-section">
              <button class="confirm-payment-btn" @click="confirmPayment">
                Đã chuyển khoản xong
              </button>
              <p class="timeout-note">
                Phiên giao dịch có hiệu lực trong
                <span class="countdown">{{ paymentCountdown }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button class="cancel-btn" @click="cancelPayment">Hủy</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import CryptoJS from 'crypto-js'
import axiosInstance from '@/plugins/axios'
import { ToastService } from '@/services/ToastService'
import { createCheckoutV2 } from '@/services/checkoutV2'
import { ownedPaymentState } from '@/services/checkoutV2Bridge'
import { CHECKOUT_V2_ENABLED } from '@/services/checkoutV2Flag'

export default {
  name: 'PaymentView',
  props: {
    token: {
      type: String,
      required: true
    }
  },

  setup (props) {
    const router = useRouter()

    const bookings = ref([])
    const isLoading = ref(true)
    const error = ref(null)
    const isProcessing = ref(false)

    // Dữ liệu khách hàng và chuyến đi
    const customerName = ref('')
    const customerPhone = ref('')
    const customerEmail = ref('')
    const destinationLocation = ref('')
    const departureTime = ref('')
    const ticketType = ref('')
    const seatNumbers = ref([])
    const totalAmount = ref(0)

    const accountNumber = ref('4536869999')
    const bankName = ref('MBBank')
    const accountName = ref('CONG TY TNHH THUONG MAI DICH VU FORESTGO') 
    const transferContent = ref('')
    const paymentCountdown = ref('')
    const countdownTimer = ref(null)
    const isExpired = ref(false)

    const addons = ref([])
    const addonsTotal = ref(0)
    const ticketTotal = ref(0)

    const passengers = ref([])
    const activePassengerIndex = ref(0)
    const pickupLocations = ref([])

    const paymentStatusCheckInterval = ref(null)

    const isSpecialTicketType = computed(() => {
      return (
        bookings.value.length > 0 &&
        bookings.value[0].ticket_type &&
        bookings.value[0].ticket_type.independent
      )
    })

    const paymentDeadlineTimestamp = ref(0)
    let animationFrameId = null
    let lastVisibilityCheck = Date.now()

    const specialTicketQuantity = computed(() => {
      return isSpecialTicketType.value ? bookings.value.length : 0
    })

    // Tạo nội dung chuyển khoản
    const generateTransferContent = () => {
      // Tạo nội dung với prefix DCX + booking ID
      const firstBookingId =
        bookings.value.length > 0 ? bookings.value[0].booking_id : ''
      return `SEVQR TKPDXC DCX${firstBookingId}`
    }

    // Tạo URL mã QR
    const qrCodeUrl = computed(() => {
      return `https://qr.sepay.vn/img?acc=${
        accountNumber.value
      }&bank=${encodeURIComponent(bankName.value)}&amount=${
        finalAmount.value || totalAmount.value
      }&des=${encodeURIComponent(transferContent.value)}`
    })

    const startAccurateCountdown = () => {
      // Clear any existing timers
      if (countdownTimer.value) {
        clearInterval(countdownTimer.value)
      }

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }

      // Set up page visibility detection
      document.addEventListener('visibilitychange', handleVisibilityChange)

      // Update immediately
      updateCountdown()

      // Also set a backup interval (still useful when tab is active)
      countdownTimer.value = setInterval(updateCountdown, 1000)
    }

    const updateCountdown = () => {
      if (!paymentDeadlineTimestamp.value) {
        console.error('Cannot update countdown: No deadline timestamp set')
        return
      }
      const now = Date.now()
      const timeRemaining = Math.max(0, paymentDeadlineTimestamp.value - now)

      // If expired, handle expiration
      if (timeRemaining <= 0) {
        clearInterval(countdownTimer.value)
        document.removeEventListener('visibilitychange', handleVisibilityChange)

        // Use setTimeout to ensure this runs even if there's a race condition
        setTimeout(() => handleExpiredPayment(), 0)
        return
      }

      // Update the display
      const minutes = Math.floor(timeRemaining / 60000)
      const seconds = Math.floor((timeRemaining % 60000) / 1000)
      paymentCountdown.value = `${minutes}:${seconds
        .toString()
        .padStart(2, '0')}`

      // Add visual warning when less than 1 minute remains
      if (minutes < 1) {
        // Add a CSS class for visual feedback
        document.querySelector('.countdown')?.classList.add('urgent')
      }

      // Use requestAnimationFrame for the next update if visible
      if (document.visibilityState === 'visible') {
        animationFrameId = requestAnimationFrame(() => {
          // Only call update every ~1 second, not on every frame
          const elapsed = Date.now() - now
          if (elapsed >= 1000) {
            updateCountdown()
          } else {
            // If less than a second has passed, wait for the remainder
            setTimeout(() => {
              animationFrameId = requestAnimationFrame(updateCountdown)
            }, 1000 - elapsed)
          }
        })
      }
    }

    // When visibility changes (tab focus changes)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab is now visible, update immediately
        const timeSinceLastCheck = Date.now() - lastVisibilityCheck
        if (timeSinceLastCheck > 1000) {
          // Only update if it's been a while
          updateCountdown()
        }
      }

      lastVisibilityCheck = Date.now()
    }

    // Updated function that doesn't cancel bookings
    const handleExpiredPayment = async () => {
      if (isExpired.value) {
        return // Prevent duplicate execution
      }

      isExpired.value = true

      try {
        // Clear all timers first
        clearAllTimers()

        // Check if payment has been made right before redirection (final check)
        try {
          if (CHECKOUT_V2_ENABLED) {
            const owned = await ownedPaymentState(createCheckoutV2())
            if (owned && owned.view.state === 'paid') {
              router.push({ name: 'PaymentSuccessView', params: { token: props.token } })
              return
            }
            throw new Error('owned-checkout-not-paid')
          }
          const response = await axiosInstance.post('/payments/status', {
            booking_ids: bookings.value.map(b => b.booking_id).join(','),
            transaction_content: transferContent.value
          })

          // If payment was found, redirect to success page
          if (response.data.status === 'paid') {
            router.push({
              name: 'PaymentSuccessView',
              params: { token: props.token }
            })
            return
          }
        } catch (finalCheckError) {
          console.error('Error during final payment check:', finalCheckError)
        }

        // Show message for better UX - but don't cancel bookings
        ToastService.warning(
          'Thời hạn thanh toán đã hết. Vui lòng đặt vé lại hoặc liên hệ nhà cung cấp.'
        )

        // REMOVED: The code that was actively cancelling bookings
        // Only redirect to home page after a brief delay
        setTimeout(() => {
          router.push('/').then(() => {
            window.location.reload()
          })
        }, 2000)
      } catch (error) {
        console.error('Error handling expired payment:', error)

        // Still redirect on error
        setTimeout(() => {
          router.push('/')
        }, 2000)
      }
    }

    const checkPaymentStatus = async () => {
      try {
        if (CHECKOUT_V2_ENABLED) {
          // The hardened path never consults the permissive legacy status endpoint: only the
          // owned checkout the server hands back may say "paid".
          const owned = await ownedPaymentState(createCheckoutV2())
          if (!owned || owned.view.state !== 'paid') return
          clearAllTimers()
          clearInterval(paymentStatusCheckInterval.value)
          router.push({ name: 'PaymentSuccessView', params: { token: props.token } })
          return
        }
        if (!bookings.value.length) return

        const response = await axiosInstance.post('/payments/status', {
          booking_ids: bookings.value.map(b => b.booking_id).join(','),
          transaction_content: transferContent.value
        })

        if (response.data.status === 'paid') {
          clearAllTimers()

          const bookingIds = bookings.value.map(b => b.booking_id)
          sessionStorage.setItem(
            `payment_completed_${bookingIds.join('_')}`,
            'true'
          )

          clearInterval(paymentStatusCheckInterval.value)
          router
            .push({
              name: 'PaymentSuccessView',
              params: { token: props.token }
            })
            .then(() => {
              window.location.reload()
            })
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
      }
    }

    const startAutoPaymentCheck = () => {
      // Clear any existing interval first
      if (paymentStatusCheckInterval.value) {
        clearInterval(paymentStatusCheckInterval.value)
      }

      // Check immediately once
      checkPaymentStatus()

      // Then set up interval for every 5 seconds
      paymentStatusCheckInterval.value = setInterval(checkPaymentStatus, 5000)
    }

    // Hàm xác nhận đã chuyển khoản
    const confirmPayment = async () => {
      isProcessing.value = true
      try {
        // Gửi yêu cầu xác nhận thanh toán lên server
        const bookingIds = bookings.value.map(b => b.booking_id)

        await axiosInstance.post('/payments/confirm', {
          booking_ids: bookingIds,
          payment_method: 'bank_transfer',
          transfer_content: transferContent.value
        })

        sessionStorage.setItem(
          `payment_completed_${bookingIds.join('_')}`,
          'true'
        )

        // Chuyển đến trang thành công
        router.push({
          name: 'PaymentSuccessView',
          params: {
            token: props.token
          }
        })
      } catch (error) {
        console.error('Error confirming payment:', error)
        ToastService.error(
          'Thanh toán chưa được xác nhận. Vui lòng thử lại sau.'
        )
      } finally {
        isProcessing.value = false
      }
    }

    // Giải mã token để lấy booking IDs
    const decryptToken = token => {
      try {
        // Khóa mã hóa cần phải giống với khóa được sử dụng trong SeatMap.vue
        const key = 'DCX_BOOKING_SECRET_KEY'
        const decrypted = CryptoJS.AES.decrypt(token, key).toString(
          CryptoJS.enc.Utf8
        )

        // Loại bỏ dấu ngoặc kép nếu có
        return decrypted.replace(/^"|"$/g, '')
      } catch (error) {
        console.error('Decrypt error:', error)
        return null
      }
    }

    const isDataIncomplete = computed(() => {
      const basicInfoIncomplete =
        !customerName.value ||
        !customerPhone.value ||
        !destinationLocation.value ||
        !departureTime.value ||
        (!totalAmount.value && !finalAmount.value && !hasAppliedDiscount.value)

      if (basicInfoIncomplete) {
        return true
      }

      const needsSeats = !isSpecialTicketType.value
      const hasSeats = seatNumbers.value && seatNumbers.value.length > 0

      return needsSeats && !hasSeats
    })

    const calculateTotalAmount = async (bookingData, ticketTypeId) => {
      try {
        // 1. Cố gắng lấy giá từ ticket_type trực tiếp (cách chính xác nhất)
        if (ticketTypeId) {
          try {
            const ticketResponse = await axiosInstance.get(
              `/ticket_types/`,
              {
                params: { ticket_type_id: ticketTypeId }
              }
            )

            if (ticketResponse.data && ticketResponse.data[0].price) {
              const price = parseFloat(ticketResponse.data[0].price)
              // Nhân với số lượng ghế đã đặt
              return price * bookingData.length
            }
          } catch (err) {
            console.error('Error fetching ticket type price:', err)
            // Tiếp tục với cách tính khác nếu lỗi
          }
        }
      } catch (error) {
        console.error('Error calculating total amount:', error)
        return 0
      }
    }

    const loadBookingData = async () => {
      isLoading.value = true
      error.value = null

      try {
        // Giải mã token để lấy booking IDs
        const decryptedBookingIds = decryptToken(props.token)

        if (!decryptedBookingIds) {
          throw new Error('Mã đặt vé không hợp lệ')
        }

        const bookingIds = decryptedBookingIds.split(',')

        // Gọi API để lấy thông tin từ booking IDs
        const bookingResults = []

        const paymentCompleted = sessionStorage.getItem(
          `payment_completed_${bookingIds.join('_')}`
        )
        if (paymentCompleted === 'true') {
          router.push({
            name: 'PaymentSuccessView',
            params: { token: props.token }
          })
          return
        }

        for (const id of bookingIds) {
          try {
            const response = await axiosInstance.get(`/bookings/`, {
              params: { booking_id: id }
            })

            if (Array.isArray(response.data) && response.data.length > 0) {
              bookingResults.push(...response.data)
            } else if (response.data && typeof response.data === 'object') {
              bookingResults.push(response.data)
            }
          } catch (err) {
            console.error(`Error fetching booking ${id}:`, err)
          }
        }

        if (bookingResults.length === 0) {
          throw new Error('Không tìm thấy thông tin đặt vé')
        }

        // After successfully loading bookings
        if (bookingResults.length > 0) {
          bookings.value = bookingResults

          // Initialize the addon and ticket totals
          addonsTotal.value = 0
          ticketTotal.value = 0
          addons.value = []

          // First, calculate the base ticket price
          ticketTotal.value = bookings.value.reduce((total, booking) => {
            return total + (booking.ticket_type?.price || 0)
          }, 0)

          totalAmount.value = ticketTotal.value
          finalAmount.value = ticketTotal.value

          if (!hasAppliedDiscount.value) {
            originalAmount.value = ticketTotal.value
          }

          // Then fetch addon data for these bookings
          const bookingIds = bookings.value.map(b => b.booking_id)

          try {
            // Get all addons for these bookings from the API
            const addonResponse = await axiosInstance.get(
              `/booking-addons/`,
              {
                params: { booking_ids: bookingIds.join(',') }
              }
            )

            if (addonResponse.data && addonResponse.data.length > 0) {
              // Group addons by product ID to combine quantities
              const addonMap = {}

              addonResponse.data.forEach(addon => {
                if (!addonMap[addon.addon_id]) {
                  addonMap[addon.addon_id] = {
                    productId: addon.addon_id,
                    name: addon.name || 'Dịch vụ bổ sung',
                    quantity: 0,
                    price: addon.price || 0,
                    total_price: 0
                  }
                }

                addonMap[addon.addon_id].quantity += addon.quantity
                addonMap[addon.addon_id].total_price +=
                  addon.price * addon.quantity
              })

              // Convert map to array
              addons.value = Object.values(addonMap)

              // Calculate total addon cost
              addonsTotal.value = addons.value.reduce((total, addon) => {
                return total + addon.total_price
              }, 0)

              // Update the total amount to include addons
              const newTotalAmount = ticketTotal.value + addonsTotal.value
              totalAmount.value = newTotalAmount

              if (hasAppliedDiscount.value && originalAmount.value > 0) {
                finalAmount.value = originalAmount.value - discountAmount.value
                totalAmount.value = finalAmount.value
              } else {
                finalAmount.value = totalAmount.value
                if (!hasAppliedDiscount.value) {
                  originalAmount.value = newTotalAmount
                }
              }

              // Store the raw amount for potential discount application later
              if (!hasAppliedDiscount.value) {
                originalAmount.value = totalAmount.value
              }
            }

            // nextTick(() => {
            //   const temp = totalAmount.value
            //   totalAmount.value = 0
            //   setTimeout(() => {
            //     totalAmount.value = temp
            //   }, 0)
            // })
          } catch (addonError) {
            console.error('Error fetching addon data:', addonError)
            totalAmount.value = ticketTotal.value
            finalAmount.value = ticketTotal.value
            if (!hasAppliedDiscount.value) {
              originalAmount.value = ticketTotal.value
            }
          }
        }

        // Lấy thông tin chi tiết về chuyến đi
        const firstBooking = bookings.value[0]
        if (!firstBooking) {
          throw new Error('Không tìm thấy thông tin đặt vé')
        }

        const tripId = firstBooking.trip_id

        if (!tripId) {
          throw new Error('Thông tin chuyến đi không đầy đủ')
        }

        // Lấy thông tin trip
        let trip = null
        try {
          const tripResponse = await axiosInstance.get(`/trips/`, {
            params: { trip_id: tripId }
          })

          if (
            Array.isArray(tripResponse.data) &&
            tripResponse.data.length > 0
          ) {
            trip = tripResponse.data[0]
          } else if (
            tripResponse.data &&
            typeof tripResponse.data === 'object'
          ) {
            trip = tripResponse.data
          }
        } catch (err) {
          console.error('Error fetching trip:', err)
          throw new Error('Không thể lấy thông tin chuyến đi')
        }

        if (!trip) {
          throw new Error('Không tìm thấy thông tin chuyến đi')
        }

        // Lấy thông tin route
        let route = null
        try {
          const routeResponse = await axiosInstance.get(
            `/routes/${trip.route_id}`
          )

          if (
            Array.isArray(routeResponse.data) &&
            routeResponse.data.length > 0
          ) {
            route = routeResponse.data[0]
          } else if (
            routeResponse.data &&
            typeof routeResponse.data === 'object'
          ) {
            route = routeResponse.data
          }
        } catch (err) {
          console.error('Error fetching route:', err)
          throw new Error('Không thể lấy thông tin tuyến đường')
        }

        if (!route) {
          throw new Error('Không tìm thấy thông tin tuyến đường')
        }

        // Cập nhật thông tin cho UI
        customerName.value = firstBooking.customer_name || 'Chưa cung cấp'
        customerPhone.value = firstBooking.customer_phone || 'Chưa cung cấp'
        customerEmail.value = firstBooking.customer_email || ''
        destinationLocation.value =
          route.destination_location || 'Không xác định'
        departureTime.value = trip.departure_datetime
        ticketType.value = firstBooking.ticket_type.type

        // Lấy danh sách ghế
        seatNumbers.value = bookings.value
          .map(b => b.slot_number)
          .filter(Boolean)
          .sort((a, b) => parseInt(a) - parseInt(b))

        // Fetch all pickup locations to get their names
        try {
          // Extract unique pickup location IDs from bookings
          const pickupLocationIds = [
            ...new Set(
              bookings.value
                .map(booking => booking.pickup_location_id)
                .filter(Boolean) // Remove any null/undefined values
            )
          ]

          if (pickupLocationIds.length > 0) {
            // Fetch only the needed pickup locations using the trip_id
            const tripId = bookings.value[0].trip_id

            // First try to fetch by trip_id
            try {
              const pickupLocationsResponse = await axiosInstance.get(
                `/pickup_locations/`,
                {
                  params: { trip_id: tripId }
                }
              )

              pickupLocations.value = Array.isArray(
                pickupLocationsResponse.data
              )
                ? pickupLocationsResponse.data
                : [pickupLocationsResponse.data]
            } catch (tripFetchError) {
              console.error(
                'Error fetching pickup locations by trip:',
                tripFetchError
              )

              // Fallback: fetch each pickup location individually if trip fetch fails
              const locationPromises = pickupLocationIds.map(locationId =>
                axiosInstance.get(`/pickup_locations/${locationId}`)
              )

              try {
                const responses = await Promise.all(locationPromises)
                pickupLocations.value = responses.map(res => res.data)
              } catch (individualFetchError) {
                console.error(
                  'Error fetching individual pickup locations:',
                  individualFetchError
                )
                pickupLocations.value = []
              }
            }
          } else {
            pickupLocations.value = []
          }
        } catch (error) {
          console.error('Error fetching pickup locations:', error)
          pickupLocations.value = []
        }

        // Build passengers information
        passengers.value = bookings.value.map((booking, index) => {
          // Find the pickup location name if available
          const pickupLocation = pickupLocations.value.find(
            loc => loc.pickup_location_id === booking.pickup_location_id
          )

          const pickupLocationName = pickupLocation
            ? `${pickupLocation.location_name}${
                pickupLocation.address ? ` (${pickupLocation.address})` : ''
              }`
            : 'Không xác định'

          return {
            name: booking.customer_name || `Hành khách ${index + 1}`,
            birthYear: booking.birth_year,
            seatNumber: booking.slot_number,
            pickupLocationId: booking.pickup_location_id,
            pickupLocationName: pickupLocationName
          }
        })

        // Sort passengers by seat number
        passengers.value.sort(
          (a, b) => parseInt(a.seatNumber) - parseInt(b.seatNumber)
        )
      } catch (err) {
        console.error('Error loading booking data:', err)
        error.value = err.message || 'Có lỗi xảy ra khi tải thông tin đặt vé'
      } finally {
        isLoading.value = false
      }
    }

    const clearAllTimers = () => {
      // Clear all intervals and timers immediately
      if (paymentStatusCheckInterval.value) {
        clearInterval(paymentStatusCheckInterval.value)
        paymentStatusCheckInterval.value = null
      }

      if (countdownTimer.value) {
        clearInterval(countdownTimer.value)
        countdownTimer.value = null
      }

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }

      // Remove event listener
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      // Also clear any server verification intervals
      if (window.serverVerificationIntervals) {
        window.serverVerificationIntervals.forEach(interval => {
          clearInterval(interval)
        })
        window.serverVerificationIntervals = []
      }

      // Set a flag to prevent further cancellation attempts
      isExpired.value = true
    }

    // Format dữ liệu hiển thị
    const formattedDepartureTime = computed(() => {
      if (!departureTime.value) return ''
      return formatDateTime(departureTime.value)
    })

    const formattedTotalAmount = computed(() => {
      return formatCurrency(totalAmount.value)
    })

    // Format helpers
    const formatDateTime = datetime => {
      return new Date(datetime).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatCurrency = amount => {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    }

    const cancelPayment = async () => {
      try {
        isProcessing.value = true

        // 1. Cancel all bookings
        await Promise.all(
          bookings.value.map(booking =>
            axiosInstance.put(
              `/bookings/?booking_id=${booking.booking_id}`,
              {
                status: 'canceled'
              }
            )
          )
        )
        // 2. Clear all intervals
        if (paymentStatusCheckInterval.value) {
          clearInterval(paymentStatusCheckInterval.value)
          paymentStatusCheckInterval.value = null
        }
        if (countdownTimer.value) {
          clearInterval(countdownTimer.value)
          countdownTimer.value = null
        }
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = null
        }
        // 3. Remove event listener
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        // 4. Redirect to home page
        ToastService.info(
          'Đơn hàng đã bị hủy. Bạn sẽ được chuyển về trang chủ trong giây lát.'
        )
        setTimeout(() => {
          router.push('/').then(() => {
            window.location.reload() // Note: no parameter here
          })
        }, 2000)
      } catch (error) {
        console.error('Error canceling payment:', error)
        ToastService.error('Có lỗi xảy ra khi hủy đặt vé. Vui lòng thử lại.')
      } finally {
        isProcessing.value = false
      }
    }

    const goBack = () => {
      router.push('/').then(() => {
        window.location.reload() // Note: no parameter here
      })
    }

    const fetchPaymentDeadline = async () => {
      try {
        if (bookings.value.length === 0) return

        // Get the first booking's creation time
        const firstBooking = bookings.value[0]

        // Check server directly for booking status and expiration time first
        try {
          const statusCheck = await axiosInstance.get(
            `/bookings/payment-status`,
            { params: { booking_id: firstBooking.booking_id } }
          )
          // If already expired or cancelled on server, handle it
          if (
            statusCheck.data.status === 'expired' ||
            statusCheck.data.status === 'canceled'
          ) {
            await handleExpiredPayment()
            return // Thêm return để thoát khỏi hàm
          }

          // Use the expiration_time from server directly if available
          if (statusCheck.data.expiration_time) {
            // Convert server timestamp to local time to match browser's Date.now()
            let serverDeadline = new Date(
              statusCheck.data.expiration_time
            ).getTime()

            // Store the absolute deadline timestamp
            paymentDeadlineTimestamp.value = serverDeadline

            // Start the countdown using the server time
            startAccurateCountdown()

            // Thiết lập kiểm tra server mỗi 3 giây
            setupServerVerification(firstBooking)

            return // THÊM RETURN ĐỂ KHÔNG THỰC HIỆN LUỒNG FALLBACK!
          }
        } catch (err) {
          console.error('Error checking payment status on load:', err)
        }

        // IMPORTANT FIX: Make sure booking time is properly parsed as local time
        const bookingTimeStr = firstBooking.booking_datetime

        // Explicitly handle the date string to ensure proper timezone processing
        // This approach ensures dates are handled consistently regardless of browser
        let bookingTime

        if (bookingTimeStr.includes('Z') || bookingTimeStr.includes('+')) {
          // String already has timezone info
          bookingTime = new Date(bookingTimeStr)
        } else {
          // Assume UTC if no timezone is specified
          bookingTime = new Date(bookingTimeStr + 'Z')
        }

        // Calculate deadline (booking time + 10 minutes)
        const deadlineTime = bookingTime.getTime() + 10 * 60 * 1000

        // Store the absolute deadline timestamp
        paymentDeadlineTimestamp.value = deadlineTime

        // Start the timer
        startAccurateCountdown()

        // Thiết lập kiểm tra server
        setupServerVerification(firstBooking)
      } catch (error) {
        console.error('Error fetching payment deadline:', error)
        ToastService.error(
          'Có lỗi xảy ra khi tải thông tin thanh toán. Vui lòng thử lại.'
        )
        router.push('/').then(() => {
          window.location.reload()
        })
      }
    }

    // Tách riêng phần kiểm tra server thành một hàm riêng để tái sử dụng
    const setupServerVerification = booking => {
      // Khởi tạo mảng toàn cục để lưu các intervals
      if (!window.serverVerificationIntervals) {
        window.serverVerificationIntervals = []
      }

      const serverVerificationInterval = setInterval(async () => {
        try {
          // Check status on server side
          const statusCheck = await axiosInstance.get(
            `/bookings/payment-status`,
            {
              params: { booking_id: booking.booking_id }
            }
          )

          // CHỈ TRIGGER handleExpiredPayment khi status là 'expired' hoặc 'canceled',
          // KHÔNG xử lý khi status là 'paid' hoặc 'paying'
          if (
            statusCheck.data.status === 'expired' ||
            (statusCheck.data.status === 'canceled' && !isProcessing.value)
          ) {
            clearInterval(serverVerificationInterval)

            // Xóa interval khỏi mảng toàn cục
            if (window.serverVerificationIntervals) {
              const index = window.serverVerificationIntervals.indexOf(
                serverVerificationInterval
              )
              if (index > -1) {
                window.serverVerificationIntervals.splice(index, 1)
              }
            }

            await handleExpiredPayment()
          }

          // Nếu đã thanh toán, chuyển đến trang success
          if (statusCheck.data.status === 'paid') {
            clearInterval(serverVerificationInterval)

            const bookingIds = bookings.value.map(b => b.booking_id)
            sessionStorage.setItem(
              `payment_completed_${bookingIds.join('_')}`,
              'true'
            )

            // Xóa interval khỏi mảng toàn cục
            if (window.serverVerificationIntervals) {
              const index = window.serverVerificationIntervals.indexOf(
                serverVerificationInterval
              )
              if (index > -1) {
                window.serverVerificationIntervals.splice(index, 1)
              }
            }

            clearAllTimers()

            router.push({
              name: 'PaymentSuccessView',
              params: { token: props.token }
            })
          }
        } catch (err) {
          console.error('Error checking server payment status:', err)
        }
      }, 30000)

      // Thêm interval vào mảng toàn cục
      window.serverVerificationIntervals.push(serverVerificationInterval)

      // Clear on component unmount
      onUnmounted(() => {
        clearInterval(serverVerificationInterval)

        // Xóa interval khỏi mảng toàn cục
        if (window.serverVerificationIntervals) {
          const index = window.serverVerificationIntervals.indexOf(
            serverVerificationInterval
          )
          if (index > -1) {
            window.serverVerificationIntervals.splice(index, 1)
          }
        }
      })
    }

    onMounted(() => {
      loadBookingData()
        .then(() => {
          fetchPaymentDeadline()
          transferContent.value = generateTransferContent()
          startAutoPaymentCheck()
        })
        .catch(err => {
          console.error('Error in initial data loading:', err)
        })
    })

    onUnmounted(() => {
      if (countdownTimer.value) {
        clearInterval(countdownTimer.value)
      }

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange)

      if (paymentStatusCheckInterval.value) {
        clearInterval(paymentStatusCheckInterval.value)
      }
    })

    const currentPassenger = computed(() => {
      if (
        passengers.value &&
        passengers.value.length > activePassengerIndex.value
      ) {
        return passengers.value[activePassengerIndex.value]
      }
      return null
    })

    // Mã giảm giá
    const discountCode = ref('')
    const discountMessage = ref('')
    const hasAppliedDiscount = ref(false)
    const isApplyingDiscount = ref(false)
    const originalAmount = ref(0)
    const discountAmount = ref(0)
    const finalAmount = ref(0)

    const applyDiscountCode = async () => {
      if (!discountCode.value || isApplyingDiscount.value) return

      isApplyingDiscount.value = true
      discountMessage.value = ''

      try {
        if (originalAmount.value === 0) {
          originalAmount.value = totalAmount.value
        }

        const response = await axiosInstance.post('/discount-codes/check', {
          code: discountCode.value,
          booking_ids: bookings.value.map(b => b.booking_id)
        })

        if (response.data.valid) {
          // Cập nhật thông tin giảm giá
          discountAmount.value = response.data.discount_amount
          finalAmount.value = response.data.final_amount
          totalAmount.value = finalAmount.value

          // Lưu trạng thái đã áp dụng mã giảm giá
          hasAppliedDiscount.value = true
          discountMessage.value = response.data.message

          // Áp dụng mã giảm giá cho đơn hàng
          await axiosInstance.post('/discount-codes/apply', {
            code: discountCode.value,
            booking_ids: bookings.value.map(b => b.booking_id)
          })

          // 🔐 Kiểm tra nếu là mã nội bộ (is_internal = true) và final_amount = 0
          if (response.data.is_internal && finalAmount.value === 0) {
            ToastService.success('Vé nội bộ đã được xác nhận tự động! Đang chuyển hướng...')
            
            // Redirect ngay về trang success sau 1.5 giây
            setTimeout(() => {
              router.push({
                name: 'PaymentSuccessView',
                params: { token: props.token }
              })
            }, 1500)
            
            return
          }

          // Cập nhật nội dung chuyển khoản sau khi áp dụng mã giảm giá
          transferContent.value = generateTransferContent()

          // Cập nhật URL mã QR với số tiền mới
          // QR Code URL sẽ tự động cập nhật vì nó là computed property dựa trên totalAmount
        } else {
          discountMessage.value = response.data.message
          hasAppliedDiscount.value = false
        }
      } catch (error) {
        console.error('Error applying discount code:', error)
        discountMessage.value = 'Có lỗi xảy ra khi áp dụng mã giảm giá'
        hasAppliedDiscount.value = false
      } finally {
        isApplyingDiscount.value = false
      }
    }

    const removeDiscountCode = () => {
      hasAppliedDiscount.value = false
      discountMessage.value = ''
      discountCode.value = ''
      discountAmount.value = 0
      totalAmount.value = originalAmount.value
      finalAmount.value = 0

      // Cập nhật nội dung chuyển khoản
      transferContent.value = generateTransferContent()
    }

    const displayAmount = computed(() => {
      // If discount applied, use finalAmount
      if (hasAppliedDiscount.value) {
        return finalAmount.value
      }
      // Otherwise use totalAmount (which is ticketTotal + addonsTotal)
      return totalAmount.value
    })

    return {
      isSpecialTicketType,
      specialTicketQuantity,
      bookings,
      isLoading,
      error,
      customerName,
      customerPhone,
      customerEmail,
      destinationLocation,
      departureTime,
      ticketType,
      seatNumbers,
      totalAmount,
      formattedDepartureTime,
      formattedTotalAmount,
      isProcessing,
      cancelPayment,
      loadBookingData,
      goBack,
      isDataIncomplete,
      qrCodeUrl,
      accountNumber,
      bankName,
      accountName,
      transferContent,
      confirmPayment,
      paymentCountdown,
      passengers,
      activePassengerIndex,
      pickupLocations,
      currentPassenger,
      discountCode,
      discountMessage,
      hasAppliedDiscount,
      isApplyingDiscount,
      originalAmount,
      discountAmount,
      finalAmount,
      applyDiscountCode,
      removeDiscountCode,
      formatCurrency,
      addons,
      addonsTotal,
      ticketTotal,
      displayAmount
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-container {
  max-width: 900px;
  margin: 2rem auto;
  padding: 0 1.5rem;
  color: #064e3b;
}

.payment-summary {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  animation: fadeIn 0.4s ease-out;

  h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1a202c;
    margin-bottom: 1rem;
    position: relative;
    padding-bottom: 0.75rem;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 3.5rem;
      height: 3px;
      background: linear-gradient(to right, #0ea5e9, #63b3ed);
      border-radius: 3px;
    }
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #064e3b;
    margin-bottom: 1.25rem;
  }
}

.booking-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  > div {
    padding: 1.75rem;
    background: linear-gradient(to bottom right, #f7fafc, #f0f5fa);
    border-radius: 0.75rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(226, 232, 240, 0.8);
    transition: transform 0.3s, box-shadow 0.3s;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 15px rgba(0, 0, 0, 0.05);
    }

    p {
      margin-bottom: 0.75rem;
      color: #075985;

      strong {
        color: #064e3b;
        font-weight: 600;
        display: inline-block;
        min-width: 110px;
      }
    }
  }

  .trip-info,
  .customer-info {
    background: white;
    border-radius: 1rem;
    padding: 1.75rem;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(226, 232, 240, 0.8);
    display: flex;
    flex-direction: column;
    min-height: 320px;

    h3 {
      color: #064e3b;
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 1.5rem;
      position: relative;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #edf2f7;
    }

    p {
      margin-bottom: 0.875rem;
      display: flex;
      flex-wrap: wrap;

      strong {
        min-width: 120px;
        color: #075985;
        font-weight: 600;
      }
    }
  }

  .customer-info {
    .primary-contact {
      margin-bottom: 1.5rem;
    }

    .all-passengers {
      h4 {
        font-size: 1.1rem;
        font-weight: 600;
        color: #075985;
        margin-bottom: 1rem;
      }
    }
  }

  .primary-contact {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e2e8f0;

    p {
      margin-bottom: 0.75rem;
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      strong {
        min-width: 120px;
        color: #075985;
        font-weight: 600;
      }
    }
  }

  .all-passengers {
    h4 {
      font-size: 1.1rem;
      font-weight: 600;
      color: #075985;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &::before {
        content: '\f007';
        font-weight: 600;
        color: #0ea5e9;
        font-size: 1rem;
      }
    }

    .passenger-tabs {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 1.25rem;

      .passenger-tab {
        padding: 0.5rem 0.875rem;
        background: #f7fafc;
        border: 1px solid #e2e8f0;
        border-radius: 0.5rem;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 0.875rem;
        font-weight: 500;
        color: #075985;

        &:hover {
          background: #edf2f7;
          border-color: #cbd5e0;
          transform: translateY(-1px);
        }

        &.active {
          background: linear-gradient(to right, #0ea5e9, #0284c7);
          color: white;
          border-color: #0284c7;
          box-shadow: 0 2px 4px rgba(66, 153, 225, 0.3);

          &:hover {
            background: linear-gradient(to right, #0284c7, #075985);
            transform: translateY(-1px);
          }
        }

        span {
          display: block;
        }
      }
    }

    .passenger-details {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 0.75rem;
      padding: 1.25rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

      p {
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;

        &:last-child {
          margin-bottom: 0;
        }

        strong {
          min-width: 110px;
          color: #064e3b;
          font-weight: 600;
          font-size: 0.9rem;
        }

        // Style for the value part
        & > :not(strong) {
          color: #075985;
          font-weight: 500;
          background: white;
          padding: 0.375rem 0.75rem;
          border-radius: 0.375rem;
          border: 1px solid #e2e8f0;
          font-size: 0.9rem;
        }
      }
    }

    .special-ticket-info {
      background: #f0fff4;
      border: 1px solid #9ae6b4;
      border-radius: 0.75rem;
      padding: 1.25rem;

      h4 {
        color: #047857;
        font-size: 1.1rem;
        font-weight: 600;
        margin-bottom: 1rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        &::before {
          content: '\f005';
          font-weight: 600;
          color: #047857;
          font-size: 1rem;
        }
      }

      p {
        color: #047857;
        font-weight: 500;

        strong {
          color: #276749;
        }
      }
    }
  }

  /* Cải thiện phần dịch vụ bổ sung */
  .addon-info {
    background: white;
    border-radius: 1rem;
    padding: 1.75rem;
    margin-bottom: 2.5rem !important;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(226, 232, 240, 0.8);

    h3 {
      color: #064e3b;
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 1.5rem;
      position: relative;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #edf2f7;
    }

    .addon-items {
      .addon-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem;
        background: #f7fafc;
        border-radius: 0.5rem;
        margin-bottom: 0.75rem;
        transition: transform 0.2s;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
        }

        .addon-name {
          font-weight: 500;
          flex: 1;
        }

        .addon-quantity {
          color: #718096;
          padding: 0 1rem;
        }

        .addon-price {
          font-weight: 600;
          color: #2c7a7b;
          white-space: nowrap;
          background: white;
          padding: 0.375rem 0.75rem;
          border-radius: 1rem;
          border: 1px solid #e6fffa;
        }
      }
    }

    .addon-total {
      display: flex;
      justify-content: space-between;
      padding: 1rem;
      border-top: 1px dashed #e2e8f0;
      margin-top: 1rem;
      font-weight: 600;

      span:last-child {
        color: #2c7a7b;
      }
    }
  }

  .price-summary {
    background: linear-gradient(135deg, #ebf4ff 0%, #e6fffa 100%);
    border-radius: 1rem;
    padding: 1.75rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.07);
    border: 1px solid rgba(190, 227, 248, 0.6);
    position: relative;
    overflow: hidden;
    border: 1px solid #90cdf4;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(to right, #0284c7, #319795);
    }

    h3 {
      color: #075985;
      font-size: 1.375rem;
      font-weight: 600;
      margin-bottom: 1.5rem;
      position: relative;
    }

    .price-breakdown {
      background: white;
      border-radius: 0.75rem;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .price-item {
      display: flex;
      justify-content: space-between;
      padding: 1rem 1.25rem;
      transition: background 0.2s;

      &:not(:last-child) {
        border-bottom: 1px solid #edf2f7;
      }

      &:hover:not(.total) {
        background: #f9fafb;
      }

      &.discount {
        background: #ebf8ff;

        span:last-child {
          color: #075985;
        }
      }

      &.total {
        background: #f0fff4;
        padding: 1.25rem;
        margin-top: 0.25rem;

        span:first-child {
          font-size: 1.125rem;
          font-weight: 600;
          color: #047857;
        }

        .total-amount {
          font-size: 1.75rem !important;
          font-weight: 600 !important;
          color: #276749 !important;
          background: white;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          box-shadow: 0 3px 10px rgba(56, 161, 105, 0.2);
          border: 1px solid #9ae6b4;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  .price-item.total {
    background: #f0fff4;
    padding: 1.25rem;
    margin-top: 0.25rem;
    font-weight: 600;
    font-size: 1.125rem;
    border-top: 1px solid #e2e8f0;
    border-radius: 0 0 0.5rem 0.5rem;
  }

  .price-item .total-amount {
    color: #2c7a7b;
    font-size: 1.25rem;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    .booking-details {
      grid-template-columns: 1fr;
    }

    .price-item.total {
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;

      .total-amount {
        font-size: 1.75rem;
      }
    }
  }
}

.payment-methods {
  margin: 2.5rem 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  h3 {
    font-size: 1.375rem;
    font-weight: 600;
    color: #1a202c;
    margin-bottom: 1.5rem;
    position: relative;
    padding-bottom: 0.5rem;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 3rem;
      height: 3px;
      background: linear-gradient(to right, #0ea5e9, #63b3ed);
      border-radius: 3px;
    }
  }
  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 0; // Loại bỏ padding có thể gây overflow

    h3 {
      font-size: 1.25rem;
      margin-bottom: 1.25rem;
    }
  }

  @media (max-width: 480px) {
    margin: 1.5rem 0;

    h3 {
      font-size: 1.125rem;
      margin-bottom: 1rem;
    }
  }
}

.bank-transfer-details {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 2.5rem;
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  margin-top: 1.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(to right, #0ea5e9, #38b2ac);
  }

  h4 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1.75rem;
    color: #064e3b;
    position: relative;
    display: inline-block;

    &::after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      width: 2.5rem;
      height: 3px;
      background: #0ea5e9;
      border-radius: 2px;
    }
  }

  // MOBILE RESPONSIVE - ĐÂY LÀ PHẦN QUAN TRỌNG NHẤT
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 1.5rem;
    margin: 1rem 0;
    border-radius: 0.75rem;
    margin-top: 1rem;

    h4 {
      font-size: 1.125rem;
      margin-bottom: 1.25rem;
      text-align: center;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem;
    gap: 1.5rem;
    border-radius: 0.75rem;
    margin: 1rem -0.5rem 0;
  }
}

.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -1.25rem;
    height: 100%;
    width: 1px;
    background: radial-gradient(#e2e8f0 20%, transparent 20%);
    background-size: 2px 8px;
    background-repeat: repeat-y;
  }

  .qr-code {
    text-align: center;
    width: 100%;

    .qr-image {
      max-width: 220px;
      width: 100%;
      height: auto;
      padding: 1rem;
      background: white;
      border-radius: 1rem;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
      border: 1px dashed #90cdf4;
      transition: all 0.3s;
      margin-bottom: 1.25rem;

      &:hover {
        transform: scale(1.03);
        border-color: #0ea5e9;
      }
    }

    .qr-note {
      margin: 0 auto;
      max-width: 90%;
      font-size: 0.9rem;
      color: #075985;
      background: #f7fafc;
      padding: 0.75rem 1rem;
      border-radius: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

    }
  }

  // MOBILE RESPONSIVE CHO QR SECTION
  @media (max-width: 768px) {
    &::after {
      content: '';
      position: absolute;
      bottom: -1.25rem;
      left: 0;
      width: 100%;
      height: 1px;
      background: radial-gradient(#e2e8f0 20%, transparent 20%);
      background-size: 8px 2px;
      background-repeat: repeat-x;
      top: auto;
      right: auto;
    }

    .qr-code {
      .qr-image {
        max-width: 200px;
        padding: 0.75rem;
      }

      .qr-note {
        font-size: 0.85rem;
        padding: 0.625rem 0.875rem;
        max-width: 95%;
      }
    }
  }

  @media (max-width: 480px) {
    .qr-code {
      .qr-image {
        max-width: 180px;
      }

      .qr-note {
        font-size: 0.8rem;
        padding: 0.5rem 0.75rem;
        flex-direction: column;
        gap: 0.25rem;
        text-align: center;

        &::before {
          font-size: 1rem;
        }
      }
    }
  }
}

.qr-code {
  text-align: center;

  .qr-image {
    max-width: 220px;
    height: auto;
    padding: 1rem;
    background: white;
    border-radius: 1rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.07);
    transition: transform 0.3s, box-shadow 0.3s;
    border: 1px solid #e2e8f0;

    &:hover {
      transform: scale(1.03);
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    }
  }

  .qr-note {
    margin-top: 1rem;
    font-size: 0.9rem;
    color: #718096;
    background: rgba(237, 242, 247, 0.8);
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.transfer-info {
  padding-left: 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  .info-item {
    display: flex;
    align-items: center;
    margin-bottom: 1.25rem;
    transition: transform 0.2s;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    &:not(.amount-item):hover {
      transform: translateX(5px);
    }

    .label {
      width: 140px;
      font-weight: 500;
      color: #075985;
      font-size: 0.95rem;
      flex-shrink: 0;
    }

    .value {
      flex-grow: 1;
      font-weight: 600;
      color: #064e3b;
      letter-spacing: 0.5px;
      font-size: 1rem;
      background: #f7fafc;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      border: 1px solid #e2e8f0;
      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.03);
      box-sizing: border-box;
      word-break: break-word;
      overflow-wrap: break-word;
      min-width: 0; // Quan trọng: cho phép flex item thu nhỏ
    }

    &.amount-item {
      margin: 1.5rem 0;
      padding: 1.25rem;
      background: linear-gradient(to right, #f0fff4, #e6fffa);
      border: 1px solid #9ae6b4;
      border-radius: 0.75rem;
      transform: none !important;
      display: block;
      box-sizing: border-box;
      width: 100%;

      .label {
        color: #047857 !important;
        font-weight: 600 !important;
        display: block;
        width: 100%;
        margin-bottom: 0.75rem;
      }

      .value.highlight-amount {
        display: block;
        font-size: 1.75rem !important;
        font-weight: 600 !important;
        color: #276749 !important;
        padding: 1rem !important;
        background: white !important;
        border: 1px solid #9ae6b4 !important;
        box-shadow: 0 4px 10px rgba(56, 161, 105, 0.15);
        letter-spacing: 0.03em !important;
        text-align: center;
        width: 100%;
        box-sizing: border-box;
      }
    }
  }

  // MOBILE RESPONSIVE CHO TRANSFER INFO - SỬA LẠI
  @media (max-width: 768px) {
    padding-left: 0; // Loại bỏ padding-left trên mobile
    width: 100%;
    max-width: 100%;
    padding: 0; // Đảm bảo không có padding gây overflow

    .info-item {
      flex-direction: column;
      align-items: stretch;
      margin-bottom: 1rem;
      width: 100%;

      &:not(.amount-item):hover {
        transform: none;
      }

      .label {
        width: 100%;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
        text-align: left;
        padding: 0; // Loại bỏ padding
      }

      .value {
        width: 100%;
        text-align: center;
        font-size: 0.95rem;
        padding: 0.625rem 0.875rem;
        margin: 0; // Loại bỏ margin
      }

      &.amount-item {
        padding: 1rem;
        margin: 1.25rem 0;
        width: 100%;

        .value.highlight-amount {
          font-size: 1.5rem !important;
          padding: 0.875rem !important;
          width: 100%;
          margin: 0;
        }
      }
    }
  }

  @media (max-width: 480px) {
    padding: 0; // Đảm bảo không có padding

    .info-item {
      margin-bottom: 0.875rem;

      .label {
        padding: 0;
      }

      .value {
        font-size: 0.9rem;
        padding: 0.5rem 0.75rem;
        word-break: break-all;
        margin: 0;
      }

      &.amount-item {
        padding: 0.875rem;
        margin: 1rem 0;

        .value.highlight-amount {
          font-size: 1.375rem !important;
          padding: 0.75rem !important;
          margin: 0;
        }
      }
    }
  }
}

// SỬA LẠI TOÀN BỘ CONTAINER
@media (max-width: 768px) {
  .payment-container {
    padding: 0 1rem;
    margin: 1.5rem auto;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .payment-summary {
    padding: 1.5rem;
    border-radius: 0.75rem;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    h2 {
      font-size: 1.5rem;
      margin-bottom: 1.5rem;
    }
  }

  // Đảm bảo tất cả children không bị overflow
  * {
    max-width: 100%;
    box-sizing: border-box;
  }
}

@media (max-width: 480px) {
  .payment-container {
    padding: 0 0.5rem; // Giảm padding container
    margin: 1rem auto;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .payment-summary {
    padding: 1rem; // Giảm padding summary
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    h2 {
      font-size: 1.375rem;
    }

    h3 {
      font-size: 1.125rem;
    }
  }

  // Đảm bảo tất cả elements fit trong màn hình
  * {
    max-width: 100%;
    box-sizing: border-box;
  }
}

@media (max-width: 768px) {
  .bank-transfer-details {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .qr-section {
    &::after {
      content: '';
      position: absolute;
      bottom: -1.25rem;
      left: 0;
      width: 100%;
      height: 1px;
      background: radial-gradient(#e2e8f0 20%, transparent 20%);
      background-size: 8px 2px;
      background-repeat: repeat-x;
      top: auto;
      right: auto;
    }
  }
}

.transfer-note {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: #fff8e1;
  padding: 1rem;
  border-radius: 0.75rem;
  margin: 1.5rem 0;
  border-left: 4px solid #f6e05e;

  i {
    color: #d69e2e;
    font-size: 1rem;
    margin-top: 0.125rem;
  }

  span {
    color: #744210;
    font-size: 0.925rem;
    line-height: 1.5;
  }

  // MOBILE RESPONSIVE CHO TRANSFER NOTE
  @media (max-width: 768px) {
    padding: 0.875rem;
    margin: 1.25rem 0;

    i {
      font-size: 0.95rem;
    }

    span {
      font-size: 0.875rem;
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.5rem;

    i {
      font-size: 0.9rem;
    }

    span {
      font-size: 0.825rem;
    }
  }
}

.confirmation-section {
  margin-top: 2rem;

  .confirm-payment-btn {
    width: 100%;
    padding: 1.125rem;
    background: linear-gradient(to right, #16a34a, #15803d);
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 1.125rem;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 6px rgba(56, 161, 105, 0.3);

    &:hover {
      background: linear-gradient(to right, #15803d, #047857);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(56, 161, 105, 0.4);
    }

    &:active {
      transform: translateY(1px);
      box-shadow: 0 2px 4px rgba(56, 161, 105, 0.4);
    }
  }

  .timeout-note {
    margin-top: 1rem;
    font-size: 0.95rem;
    color: #075985;
    text-align: center;

    .countdown {
      font-weight: 600;
      color: #e53e3e;
      background: rgba(254, 242, 242, 0.7);
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      animation: pulse 2s infinite;
    }
  }

  // MOBILE RESPONSIVE CHO CONFIRMATION SECTION
  @media (max-width: 768px) {
    margin-top: 1.5rem;

    .confirm-payment-btn {
      padding: 1rem;
      font-size: 1rem;
    }

    .timeout-note {
      font-size: 0.9rem;
      padding: 0 0.5rem;

      .countdown {
        display: inline-block;
        margin-top: 0.25rem;
      }
    }
  }

  @media (max-width: 480px) {
    .confirm-payment-btn {
      padding: 0.875rem;
      font-size: 0.95rem;
    }

    .timeout-note {
      font-size: 0.85rem;
    }
  }
}

.confirmation-section {
  margin-top: 2rem;

  .confirm-payment-btn {
    width: 100%;
    padding: 1.125rem;
    background: linear-gradient(to right, #16a34a, #15803d);
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 1.125rem;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 6px rgba(56, 161, 105, 0.3);

    &:hover {
      background: linear-gradient(to right, #15803d, #047857);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(56, 161, 105, 0.4);
    }

    &:active {
      transform: translateY(1px);
      box-shadow: 0 2px 4px rgba(56, 161, 105, 0.4);
    }
  }

  .timeout-note {
    margin-top: 1rem;
    font-size: 0.95rem;
    color: #075985;
    text-align: center;

    .countdown {
      font-weight: 600;
      color: #e53e3e;
      background: rgba(254, 242, 242, 0.7);
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      animation: pulse 2s infinite;
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;

  button {
    padding: 0.875rem 1.75rem;
    border-radius: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;

    &.cancel-btn {
      background: #e2e8f0;
      color: #075985;

      &:hover {
        background: #cbd5e0;
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}

/* Loading & Error States */
.loading-state,
.error-state,
.data-warning {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  text-align: center;
  animation: fadeIn 0.4s ease-out;
}

.loading-state {
  p {
    font-size: 1.25rem;
    color: #075985;
    margin-top: 1.5rem;

    &::before {
      content: '';
      display: block;
      width: 40px;
      height: 40px;
      border: 4px solid #e2e8f0;
      border-top-color: #0ea5e9;
      border-radius: 50%;
      margin: 0 auto 1.5rem;
      animation: spin 1.5s linear infinite;
    }
  }
}

.error-state {
  gap: 1.5rem;

  p {
    font-size: 1.125rem;
    color: #e53e3e;
    background: #fed7d7;
    padding: 1rem 1.5rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    &::before {
      content: '\f071';
      font-weight: 600;
    }
  }

  .retry-btn {
    padding: 0.875rem 2rem;
    border-radius: 0.5rem;
    background: linear-gradient(to right, #0ea5e9, #0284c7);
    color: white;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.3s;
    box-shadow: 0 4px 6px rgba(66, 153, 225, 0.3);

    &:hover {
      background: linear-gradient(to right, #0284c7, #075985);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(66, 153, 225, 0.4);
    }
  }

  .cancel-btn {
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    background: #e2e8f0;
    color: #075985;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;

    &:hover {
      background: #cbd5e0;
      transform: translateY(-2px);
    }
  }
}

.data-warning {
  background: white;
  padding: 2.5rem;

  p {
    font-size: 1.125rem;
    color: #7b341e;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fff8e1;
    padding: 1.25rem;
    border-radius: 0.75rem;
    border-left: 4px solid #f6ad55;
    text-align: left;

    i {
      color: #dd6b20;
      font-size: 1.5rem;
      flex-shrink: 0;
    }
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
}

/* Enhanced Price Summary */
.price-summary {
  background: linear-gradient(to bottom right, #f7faff, #eef5ff);
  border: 1px solid rgba(66, 153, 225, 0.2);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.08);

  h3 {
    color: #075985;
    font-size: 1.375rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    position: relative;
    text-align: center;
    padding-bottom: 0.75rem;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background: linear-gradient(to right, #0284c7, #319795);
      border-radius: 3px;
    }
  }
}

.price-breakdown {
  background: white;
  border-radius: 0.5rem;
  padding: 0.5rem 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.price-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f5fa;
  font-size: 1rem;

  &:last-child {
    border-bottom: none;
  }

  &.discount {
    color: #075985;
    font-weight: 500;
    background-color: #ebf8ff;
  }

  &.total {
    font-weight: 600;
    background-color: #f7fafc;
    font-size: 1.125rem;
    color: #1a202c;
    padding: 1rem;
    margin-top: 0.25rem;
    border-top: 1px solid #e2e8f0;
    border-radius: 0 0 0.5rem 0.5rem;
  }

  .total-amount {
    color: #2c7a7b;
    font-size: 1.25rem;
    font-weight: 600;
  }
}

/* Enhanced bank transfer section */
.info-item.amount-item {
  margin: 1.25rem 0;
  padding: 0.75rem;
  background: linear-gradient(to right, #f0fff4, #e6fffa);
  border: 1px solid #9ae6b4;
  border-radius: 0.5rem;
  transform: none !important;

  .label {
    color: #047857 !important;
    font-weight: 600 !important;
  }

  .value.highlight-amount {
    font-size: 1.25rem !important;
    font-weight: 600 !important;
    color: #276749 !important;
    padding: 0.75rem !important;
    background: white !important;
    border: 1px solid #9ae6b4 !important;
    box-shadow: 0 2px 5px rgba(56, 161, 105, 0.15);
    letter-spacing: 0.03em !important;
  }
}

/* Better responsive design */
@media (max-width: 640px) {
  .price-summary {
    padding: 1.25rem;
  }

  .price-item {
    padding: 0.75rem;

    &.total {
      flex-direction: column;
      gap: 0.5rem;
      align-items: center;

      .total-amount {
        font-size: 1.5rem;
      }
    }
  }

  .info-item.amount-item {
    flex-direction: column;

    .label {
      margin-bottom: 0.5rem;
    }

    .value.highlight-amount {
      text-align: center;
      font-size: 1.5rem !important;
    }
  }
}

.discount-section {
  background: linear-gradient(to bottom right, #f7faff, #eef5ff);
  border-radius: 1rem;
  padding: 1.75rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(66, 153, 225, 0.15);
  box-shadow: 0 4px 15px rgba(66, 153, 225, 0.08);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  h3 {
    color: #075985;
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1.25rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      width: 3.5rem;
      height: 3px;
      background: linear-gradient(to right, #0ea5e9, #63b3ed);
      border-radius: 3px;
    }
  }

  .discount-form {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    .discount-input {
      flex: 1;
      border: 2px solid #e2e8f0;
      border-radius: 0.5rem;
      padding: 0.75rem 1rem;
      font-size: 1rem;
      transition: all 0.2s;
      background-color: white;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      min-width: 0; // Quan trọng: cho phép flex item thu nhỏ

      &:focus {
        outline: none;
        border-color: #0ea5e9;
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
      }

      &:disabled {
        background-color: #f7fafc;
        cursor: not-allowed;
        opacity: 0.7;
      }
    }

    .apply-discount-btn {
      background: linear-gradient(to right, #0ea5e9, #0284c7);
      color: white;
      font-weight: 600;
      padding: 0 1.5rem;
      border: none;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.3s;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 120px;
      flex-shrink: 0; // Không cho button bị thu nhỏ
      box-sizing: border-box;

      &:hover:not(:disabled) {
        background: linear-gradient(to right, #0284c7, #075985);
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(66, 153, 225, 0.3);
      }

      &:disabled {
        background: #cbd5e0;
        cursor: not-allowed;
        opacity: 0.7;
      }

      &.applying {
        position: relative;
        pointer-events: none;

        i {
          margin-right: 0.5rem;
        }
      }
    }
  }

  // MOBILE RESPONSIVE CHO DISCOUNT SECTION - SỬA LẠI
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border-radius: 0.75rem;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    h3 {
      font-size: 1.125rem;
      margin-bottom: 1rem;
    }

    .discount-form {
      flex-direction: column;
      gap: 0.75rem;
      width: 100%;

      .discount-input {
        width: 100%;
        padding: 0.875rem 1rem;
        box-sizing: border-box;
        flex: none; // Loại bỏ flex trên mobile
      }

      .apply-discount-btn {
        width: 100%;
        padding: 0.875rem;
        min-width: auto;
        box-sizing: border-box;
        flex: none; // Loại bỏ flex trên mobile
      }
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    margin: 1rem 0 1.5rem; // Loại bỏ negative margin
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    .discount-form {
      .discount-input {
        padding: 0.75rem 0.875rem;
        font-size: 0.95rem;
        width: 100%;
      }

      .apply-discount-btn {
        padding: 0.75rem;
        font-size: 0.95rem;
        width: 100%;
      }
    }
  }
}

.travel-ticket {
  --brand-green: #16a34a;
  --brand-green-dark: #064e3b;
  --brand-sky: #0ea5e9;
  --brand-sky-dark: #075985;
  position: relative;
  max-width: 1120px;
  margin: 0 auto;
  padding: clamp(1.25rem, 3vw, 2.5rem);
  border: 1px solid rgba(14, 165, 233, 0.22);
  border-radius: 2rem;
  background:
    radial-gradient(circle at 92% 4%, rgba(14, 165, 233, 0.18), transparent 28%),
    radial-gradient(circle at 8% 95%, rgba(22, 163, 74, 0.14), transparent 32%),
    linear-gradient(135deg, #effdf6 0%, #e0f2fe 48%, #ffffff 100%);
  box-shadow: 0 30px 90px rgba(14, 165, 233, 0.16);
  overflow: hidden;

  > * {
    position: relative;
    z-index: 1;
  }

  h2::after,
  h3::after {
    display: none;
  }

  .ticket-hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 1.75rem;

    h2 {
      margin: 0;
      padding: 0;
      color: #052e2b;
      font-size: clamp(2rem, 4vw, 3.25rem);
      font-weight: 600;
      letter-spacing: -0.04em;
      line-height: 1.02;
    }

    p {
      max-width: 640px;
      margin: 0.75rem 0 0;
      color: rgba(6, 78, 59, 0.72);
      font-size: 1rem;
      font-weight: 600;
      line-height: 1.6;
    }
  }

  .ticket-eyebrow,
  .discount-kicker,
  .bank-heading span {
    display: inline-flex;
    color: var(--brand-sky-dark);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    line-height: 1;
    text-transform: uppercase;
  }

  .ticket-countdown {
    flex-shrink: 0;
    min-width: 132px;
    padding: 1rem 1.125rem;
    border: 1px solid rgba(22, 163, 74, 0.22);
    border-radius: 1.25rem;
    background: rgba(255, 255, 255, 0.78);
    box-shadow: 0 18px 42px rgba(6, 78, 59, 0.12);
    text-align: center;
    backdrop-filter: blur(12px);

    span {
      display: block;
      color: var(--brand-green-dark);
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    strong {
      display: block;
      margin-top: 0.2rem;
      color: var(--brand-green-dark);
      font-size: 2rem;
      font-weight: 600;
      line-height: 1;
    }
  }

  .ticket-divider {
    position: relative;
    margin: 1.8rem 0;
    border-top: 2px dashed rgba(14, 165, 233, 0.34);

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      width: 2.25rem;
      height: 2.25rem;
      border: 1px solid rgba(14, 165, 233, 0.26);
      border-radius: 999px;
      background: #eefbf8;
      transform: translateY(-50%);
    }

    &::before {
      left: -3.6rem;
    }

    &::after {
      right: -3.6rem;
    }
  }

  .booking-details {
    margin: 1.5rem 0 0;

    > div {
      border: 1px solid rgba(14, 165, 233, 0.16);
      border-radius: 1.35rem;
      background: rgba(255, 255, 255, 0.82);
      box-shadow: 0 16px 44px rgba(14, 165, 233, 0.08);
      backdrop-filter: blur(10px);
    }

    h3,
    h4 {
      color: var(--brand-green-dark);
    }
  }

  .price-summary {
    border: 1px solid rgba(22, 163, 74, 0.22);
    background: linear-gradient(135deg, rgba(236, 253, 245, 0.92), rgba(224, 242, 254, 0.82));
    box-shadow: 0 20px 54px rgba(22, 163, 74, 0.12);

    h3 {
      color: var(--brand-green-dark);
    }
  }

  .price-breakdown {
    border: 1px solid rgba(14, 165, 233, 0.12);
    border-radius: 1rem;
    box-shadow: none;
  }

  .price-item.discount {
    color: #047857;
    background: #ecfdf5;
  }

  .price-item.total {
    color: #052e2b;
    background: linear-gradient(90deg, #ecfdf5, #e0f2fe);
  }

  .payment-methods {
    margin-top: 0;
  }

  .discount-section {
    padding: clamp(1rem, 2vw, 1.5rem);
    border: 1px solid rgba(14, 165, 233, 0.18);
    border-radius: 1.5rem;
    background: rgba(255, 255, 255, 0.76);
    box-shadow: 0 20px 54px rgba(14, 165, 233, 0.1);
    backdrop-filter: blur(12px);

    &.applied {
      border-color: rgba(22, 163, 74, 0.34);
      background: linear-gradient(135deg, rgba(236, 253, 245, 0.9), rgba(240, 249, 255, 0.84));
    }

    h3 {
      margin: 0;
      color: #052e2b;
      font-size: 1.35rem;
      font-weight: 600;
      letter-spacing: -0.02em;
    }
  }

  .discount-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .discount-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    max-width: 100%;
    padding: 0.65rem 0.85rem;
    border: 1px solid rgba(22, 163, 74, 0.32);
    border-radius: 999px;
    background: rgba(220, 252, 231, 0.9);
    color: #166534;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.25;
    box-shadow: 0 10px 28px rgba(22, 163, 74, 0.12);
  }

  .discount-form {
    align-items: stretch;

    .discount-input {
      border: 1px solid rgba(14, 165, 233, 0.28);
      border-radius: 999px;
      padding: 0.95rem 1.2rem;
      background: white;
      color: #052e2b;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;

      &::placeholder {
        color: rgba(7, 89, 133, 0.48);
        font-weight: 600;
        letter-spacing: 0;
        text-transform: none;
      }

      &:focus {
        border-color: var(--brand-sky);
        box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.14);
      }
    }

    .apply-discount-btn {
      min-height: 52px;
      border-radius: 999px;
      background: linear-gradient(135deg, var(--brand-green), var(--brand-sky));
      box-shadow: 0 14px 30px rgba(14, 165, 233, 0.2);
      font-weight: 600;

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #15803d, var(--brand-sky-dark));
        box-shadow: 0 18px 38px rgba(14, 165, 233, 0.26);
      }
    }
  }

  .discount-message {
    border-radius: 1rem;
    padding: 0.85rem 1rem;
    font-weight: 600;

    &.success {
      border: 1px solid rgba(22, 163, 74, 0.2);
      background: rgba(220, 252, 231, 0.72);
      color: #166534;
    }

    &.error {
      border: 1px solid rgba(239, 68, 68, 0.2);
      background: #fef2f2;
      color: #b91c1c;
    }
  }

  .discount-summary {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1rem;
    align-items: center;
    margin-top: 1rem;
    padding: 1rem;
    border: 1px solid rgba(22, 163, 74, 0.18);
    border-radius: 1.25rem;
    background: rgba(255, 255, 255, 0.72);
  }

  .discount-info {
    display: grid;
    gap: 0.65rem;
  }

  .discount-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    color: #075985;
    font-size: 0.95rem;
    font-weight: 600;

    &.highlight {
      color: #15803d;
    }

    &.total {
      margin-top: 0.25rem;
      padding-top: 0.75rem;
      border-top: 1px dashed rgba(14, 165, 233, 0.26);
      color: #052e2b;
      font-size: 1.05rem;
      font-weight: 600;
    }
  }

  .remove-discount-btn {
    height: max-content;
    border: 1px solid rgba(22, 163, 74, 0.18);
    border-radius: 999px;
    padding: 0.7rem 0.95rem;
    background: white;
    color: #047857;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      border-color: rgba(22, 163, 74, 0.4);
      box-shadow: 0 10px 24px rgba(22, 163, 74, 0.12);
    }
  }

  .bank-heading {
    margin: 2rem 0 1rem;

    h3 {
      margin: 0.25rem 0 0;
      color: #052e2b;
      font-size: 1.45rem;
      font-weight: 600;
      letter-spacing: -0.02em;
    }
  }

  .bank-transfer-details {
    border: 1px solid rgba(14, 165, 233, 0.2);
    border-radius: 1.5rem;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 24px 64px rgba(14, 165, 233, 0.12);
    backdrop-filter: blur(12px);

    &::before {
      height: 6px;
      background: linear-gradient(90deg, var(--brand-green), var(--brand-sky));
    }

    h4 {
      color: #052e2b;
      font-weight: 600;

      &::after {
        background: linear-gradient(90deg, var(--brand-green), var(--brand-sky));
      }
    }
  }

  .info-item.amount-item {
    border: 1px solid rgba(22, 163, 74, 0.26);
    border-radius: 1rem;
    background: linear-gradient(90deg, #ecfdf5, #e0f2fe);

    .value.highlight-amount {
      border: 1px solid rgba(14, 165, 233, 0.18) !important;
      border-radius: 0.85rem !important;
      color: #065f46 !important;
    }
  }

  .confirmation-section .confirm-payment-btn {
    border-radius: 999px;
    background: linear-gradient(135deg, var(--brand-green), var(--brand-sky));
    font-weight: 600;
    box-shadow: 0 16px 36px rgba(22, 163, 74, 0.24);

    &:hover {
      background: linear-gradient(135deg, #15803d, var(--brand-sky-dark));
    }
  }

  .timeout-note .countdown {
    color: #b91c1c;
    border: 1px solid rgba(239, 68, 68, 0.16);
    border-radius: 999px;
    background: #fef2f2;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 1.35rem;

    .ticket-hero,
    .discount-heading,
    .discount-summary {
      grid-template-columns: 1fr;
      flex-direction: column;
    }

    .ticket-countdown {
      width: 100%;
    }

    .discount-summary {
      display: flex;
      align-items: stretch;
    }

    .discount-row {
      flex-direction: column;
      gap: 0.2rem;
    }
  }
}

</style>
